import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib/types";
import { articles } from "@/data/articles";
import { Menu, X } from "lucide-react";
import RieeLogo from "@/components/RieeLogo";

interface ArticleHeaderProps {
  articleId: number;
  sections: { id: string; title: string }[];
}

export default function ArticleHeader({ articleId, sections }: ArticleHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === articleId);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setMobileOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-3 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-md border-b border-[var(--border)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 flex items-center gap-4 h-14">
        {/* Logo / back */}
        <button
          onClick={() => navigate(ROUTE_PATHS.HOME)}
          className="flex items-center gap-2 flex-shrink-0 group"
          aria-label="Volver al inicio"
        >
          <RieeLogo height={scrolled ? 28 : 24} />
        </button>

        {/* Divider */}
        <div className={`w-px h-5 ${scrolled ? "bg-gray-300" : "bg-white/30"} hidden sm:block`} />

        {/* Article short title */}
        {scrolled && article && (
          <span className="text-xs text-gray-600 font-medium truncate hidden md:block max-w-xs">
            {article.title.substring(0, 60)}…
          </span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                scrolled
                  ? "text-gray-600 hover:text-[var(--riee-purple)] hover:bg-purple-50"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
            >
              {s.title}
            </button>
          ))}
        </nav>

        {/* Mobile hamburger */}
        <button
          className={`lg:hidden p-1.5 rounded ${scrolled ? "text-gray-600" : "text-white"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menú de navegación"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile nav dropdown */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className="w-full text-left px-5 py-3 text-sm text-gray-700 hover:text-[var(--riee-purple)] hover:bg-purple-50 border-b border-gray-50 last:border-none"
            >
              {s.title}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}
